import React, { memo, useCallback, useEffect, useRef } from 'react'

import { Button, Icon, Textarea, ImageAddPlus } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import References from './Components/References/References'

const uploadIcon = <Icon svgComponent={ImageAddPlus} size={iconSize} color="darker" notSpaced />

// Keep in sync with the textarea's max-height in _image_assistant_panel.scss.
const PROMPT_MAX_HEIGHT = 280

function Composer({
  prompt,
  attachments,
  tags,
  fileInputRef,
  sendLabel,
  placeholder = null,
  notice = null,
  isSendDisabled,
  isUploadDisabled,
  isUploadHidden = false,
  onPromptChange,
  onTagClick,
  onUploadClick,
  onRemoveAttachment,
  onFilesChange,
  onSend,
}) {
  const t = useTranslation()
  const promptRef = useRef(null)
  // Triangle's Textarea types its forwarded ref as a callback (`innerRef: func`),
  // so hand it one instead of the ref object.
  const setPromptRef = useCallback((element) => { promptRef.current = element }, [])

  // The textarea hugs its content up to PROMPT_MAX_HEIGHT, then scrolls. Driven
  // from the value (not the change event) so programmatic resets - sending, tag
  // insertion - shrink it back too.
  useEffect(() => {
    const textarea = promptRef.current
    if (!textarea) {
      return
    }

    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, PROMPT_MAX_HEIGHT)}px`
  }, [prompt])

  const handleKeyDown = (event) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (!isSendDisabled) {
        onSend()
      }
    }
  }

  return (
    <div className="image-assistant-panel__composer">
      {notice}
      <div className="image-assistant-panel__composer-card">
        {attachments.length > 0 && (
          <References
            attachments={attachments}
            tags={tags}
            onRemoveAttachment={onRemoveAttachment}
            onTagClick={onTagClick}
          />
        )}
        <div className="image-assistant-panel__composer-input">
          <Textarea
            ref={setPromptRef}
            id="ai-create-prompt"
            rows={1}
            isNotResizable
            inputClassName="image-assistant-panel__prompt-field-wrap"
            className="image-assistant-panel__prompt-field cgt-button-text-md"
            placeholder={placeholder ?? t('shared.composer.placeholder')}
            value={prompt}
            onChange={onPromptChange}
            onKeyDown={handleKeyDown}
          />
          <div className="image-assistant-panel__composer-actions">
            {!isUploadHidden && (
              <Button
                variant="only-icon"
                type="secondary"
                size="small"
                actionType="button"
                customClass="image-assistant-panel__upload-button"
                icon={uploadIcon}
                aria-label="Upload image"
                onClick={onUploadClick}
                disabled={isUploadDisabled}
              />
            )}
            <Button
              type="ghost"
              size="small"
              actionType="button"
              customClass="image-assistant-panel__send-button cgt-body-base-semibold"
              onClick={onSend}
              disabled={isSendDisabled}
            >
              {sendLabel}
            </Button>
          </div>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="image-assistant-panel__file-input"
        onChange={onFilesChange}
        aria-hidden
        tabIndex={-1}
      />
    </div>
  )
}

Composer.propTypes = {
  prompt: string.isRequired,
  attachments: arrayOf(shape({ id: number.isRequired, url: string.isRequired })).isRequired,
  tags: arrayOf(string).isRequired,
  fileInputRef: shape({ current: object }).isRequired,
  sendLabel: string.isRequired,
  placeholder: string,
  notice: node,
  isSendDisabled: bool.isRequired,
  isUploadDisabled: bool.isRequired,
  isUploadHidden: bool,
  onPromptChange: func.isRequired,
  onTagClick: func.isRequired,
  onUploadClick: func.isRequired,
  onRemoveAttachment: func.isRequired,
  onFilesChange: func.isRequired,
  onSend: func.isRequired,
}

export default memo(Composer)
